import React from 'react';

import { getDayNames, getMonthNames } from 'src/util/i18n/constants';
import { getLocale } from 'src/util/i18n/handler';

import rangesFor from '../array';
import {
  AllDaysOfWeek, AllMonthsOfYear, DAY_PUBLIC_HOLIDAY,
} from '../constants';
import {
  formatTimeObject,
  isSimple,
  isValidTimeOfUse,
} from '../timeOfUse';

/**
 * Formats a range (start to finish) using provided labels.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {object} range - Range containing time boundaries
 * @param {object} labels - Label mapping object
 * @returns {string | null} Formatted range string
 */
export const buildRange = (intl, range, labels) => {
  if (!intl || !range || !labels
    || Object.keys(range).length === 0 || Object.keys(labels).length === 0) {
    return null;
  }

  const { start, finish } = range || {};

  if (start === finish) {
    return labels[start] || '';
  }

  const getLabel = (key) => labels[key] || '';

  if (start && finish) {
    return intl.formatMessage(
      {
        id: 'util.i18n.time_of_use.build_range.time_range.start_set_finish_set',
        defaultMessage: '{start} to {finish}',
      },
      { start: getLabel(start), finish: getLabel(finish) },
    );
  }

  if (start && !finish) {
    return intl.formatMessage(
      {
        id: 'util.i18n.time_of_use.build_range.time_range.start_set_finish_unset',
        defaultMessage: 'from {start}',
      },
      { start: getLabel(start) },
    );
  }

  if (!start && finish) {
    return intl.formatMessage(
      {
        id: 'util.i18n.time_of_use.build_range.time_range.start_unset_finish_set',
        defaultMessage: 'until {finish}',
      },
      { finish: getLabel(finish) },
    );
  }

  return null;
};

/**
 * Builds formatted timespans from time-of-day objects.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {Array<object>} timesOfDay - Array of time range objects
 * @param {string} locale - The locale for formatting
 * @returns {Array<string>} Array of formatted timespans
 */
const buildTimespans = (intl, timesOfDay, locale) => {
  if (!Array.isArray(timesOfDay)) {
    return [];
  }

  return timesOfDay.map((timeRange) => {
    if (!timeRange || !timeRange.start || !timeRange.finish) {
      return '';
    }

    const startTime = formatTimeObject(timeRange.start, locale);
    const finishTime = formatTimeObject(timeRange.finish, locale);

    return intl.formatMessage({
      id: 'util.i18n.time_of_use.build_timespans.times_of_day.clock_timerange',
      defaultMessage: '{startTime}-{finishTime}',
    }, { startTime, finishTime });
  }).filter(Boolean); // Remove empty strings
};

/**
 * Builds the display list from month ranges, day ranges, and time ranges.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {object} element - The time of useable object containing time restrictions
 * @param {string} locale - The locale for formatting
 * @returns {Array<string>} Array of formatted display strings
 */
const buildTimeOfUseRestrictionList = (intl, element, locale) => {
  const { monthsOfYear, daysOfWeek, timesOfDay } = element;

  // Get month and day name mappings
  const monthNamesList = getMonthNames(intl.formatMessage);
  const dayNamesList = getDayNames(intl.formatMessage);

  // Calculate ranges
  const monthsOfYearRanges = rangesFor(monthsOfYear, AllMonthsOfYear, true);
  const daysOfWeekRanges = rangesFor(daysOfWeek, AllDaysOfWeek.slice(0, 7), true);

  // Format ranges
  const monthRanges = monthsOfYearRanges.map((range) => buildRange(intl, range, monthNamesList));
  const dayRanges = daysOfWeekRanges.map((range) => buildRange(intl, range, dayNamesList));

  // Build initial display list
  const displayList = [...monthRanges, ...dayRanges];

  // Add public holidays if included
  if (daysOfWeek.includes(DAY_PUBLIC_HOLIDAY)) {
    displayList.push(
      intl.formatMessage({
        id: 'util.i18n.time_of_use.build_time_of_use_restriction_list.public_holidays.label',
        defaultMessage: 'Public holidays',
      }),
    );
  }

  // Add time ranges
  const timespanStrings = buildTimespans(intl, timesOfDay, locale);
  if (timespanStrings.length > 0) {
    if (timespanStrings.length === 1) {
      const timeRangeMessage = intl.formatMessage({
        id: 'util.i18n.time_of_use.build_time_of_use_restriction_list.times_of_day.list',
        defaultMessage: 'between {timesOfDayList}',
      }, { timesOfDayList: timespanStrings[0] });
      displayList.push(timeRangeMessage);
    } else {
      // Handle multiple time ranges with proper internationalized list formatting
      const timespanList = intl.formatList(timespanStrings, { type: 'conjunction' });
      const timespanMessage = intl.formatMessage({
        id: 'util.i18n.time_of_use.build_time_of_use_restriction_list.times_of_day.list',
        defaultMessage: 'between {timesOfDayList}',
      }, { timesOfDayList: timespanList });
      displayList.push(timespanMessage);
    }
  }

  return displayList.filter(Boolean); // Remove any empty strings
};

/**
 * Given a time object, return the time of use conditions.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {object} timeOfUseable - The time configuration object
 * @param {boolean} [timeOfUseable.ignoreDaylightSavings] - Whether to ignore daylight savings time
 * @param {boolean} [timeOfUseable.ignorePublicHolidays] - Whether to ignore public holidays
 * @param {string} [timeOfUseable.timezone] - The timezone to use for time calculations
 * @returns {Array<string> | null} An array of formatted condition strings, or null
 */
export const buildTimeOfUseConditions = (intl, timeOfUseable) => {
  if (!intl || !timeOfUseable || Object.keys(timeOfUseable).length === 0) {
    return null;
  }

  const { ignoreDaylightSavings, ignorePublicHolidays, timezone } = timeOfUseable;
  const conditions = [];

  if (ignoreDaylightSavings) {
    conditions.push(
      intl.formatMessage({
        id: 'util.i18n.time_of_use.build_time_of_use_conditions.ignore_day_light_savings.label',
        defaultMessage: 'ignoring daylight savings time',
      }),
    );
  }

  if (ignorePublicHolidays) {
    conditions.push(
      intl.formatMessage({
        id: 'util.i18n.time_of_use.build_time_of_use_conditions.ignore_public_holidays.label',
        defaultMessage: 'public holidays treated as calendar days',
      }),
    );
  }

  if (timezone) {
    const timezoneMessage = intl.formatMessage({
      id: 'util.i18n.time_of_use.build_time_of_use_conditions.using_explicit_timezone.label',
      defaultMessage: "using explicit timezone ''{timezone}''",
    }, { timezone });
    conditions.push(timezoneMessage);
  }

  return conditions;
};

/**
 * Builds a localized text representation of time-of-use restrictions.
 *
 * This function analyzes a time of useable element object containing time
 * restrictions (months, days, times) and returns a human-readable, internationalized
 * string describing when the time of useable element applies.
 * It handles various scenarios from "At all times" to complex combinations of date/time ranges.
 * @param {import('react-intl').IntlShape} intl - The internationalization object for
 *   formatting messages
 * @param {object} element - The time of useable element containing time-of-use restrictions
 * @param {Array<string>} element.monthsOfYear - Array of month constants
 *   (e.g., [MONTH_JANUARY, MONTH_FEBRUARY])
 * @param {Array<string>} element.daysOfWeek - Array of day constants
 *   (e.g., [DAY_MONDAY, DAY_TUESDAY])
 * @param {Array<object>} element.timesOfDay - Array of time range objects with
 *   start/finish properties containing hours, minutes, seconds
 * @returns {string|null} Localized string describing the time-of-use restrictions,
 *   or null if invalid input
 * @example
 * // Returns "At all times" for unrestricted time of useable element
 * buildDescription(intl, {
 *   monthsOfYear: AllMonthsOfYear,
 *   daysOfWeek: AllDaysOfWeek,
 *   timesOfDay: [AllTimesOfDay]
 * });
 * @example
 * // Returns "January to March, Monday to Friday, between 09:00-17:00"
 * buildDescription(intl, {
 *   monthsOfYear: [MONTH_JANUARY, MONTH_FEBRUARY, MONTH_MARCH],
 *   daysOfWeek: [DAY_MONDAY, DAY_TUESDAY, DAY_WEDNESDAY, DAY_THURSDAY, DAY_FRIDAY],
 *   timesOfDay: [{ start: { hours: 9, minutes: 0 }, finish: { hours: 17, minutes: 0 } }]
 * });
 */
export const buildDescription = (intl, element) => {
  if (!intl) {
    return null;
  }

  if (!isValidTimeOfUse(element)) {
    return null;
  }

  if (isSimple(element)) {
    return intl.formatMessage({
      id: 'util.i18n.time_of_use.get_description.at_all_times.text',
      defaultMessage: 'At all times',
    });
  }

  const locale = getLocale();

  const restrictionList = buildTimeOfUseRestrictionList(intl, element, locale);

  if (restrictionList.length === 0) return '';
  if (restrictionList.length === 1) return restrictionList[0];

  return intl.formatList(restrictionList, { type: 'conjunction' });
};

/**
 * Builds a React element containing formatted time-of-use conditions.
 * Returns null if no conditions are present.
 * @param {import('react-intl').IntlShape} intl - The internationalization object for
 *  formatting messages
 * @param {object} element - The time of useable object containing time-of-use configuration
 * @param {boolean} [element.ignoreDaylightSavings] - Whether to ignore daylight savings time
 * @param {boolean} [element.ignorePublicHolidays] - Whether to ignore public holidays
 * @param {string} [element.timezone] - The timezone to use for time calculations
 * @returns {React.ReactElement|null} A div element with formatted conditions or null if
 *  no conditions
 */
export const buildTimeOfUseableElementConditions = (intl, element) => {
  const conditions = buildTimeOfUseConditions(intl, element);
  if (!conditions || conditions.length === 0) {
    return null;
  }

  return (<div>{`(${intl.formatList(conditions, { type: 'unit' })})`}</div>);
};
